import { useDownloadExportStore } from '../../stores/useDownloadExportStore';
import { getBrowserDownloadSupport } from './browserDownloadSupport';
import { classifyDownloadError } from './downloadError';
import { downloadStudyDicomZip } from './downloadStudyDicomZip';
import { downloadCurrentImageJpeg } from './downloadCurrentImageJpeg';
import { downloadStudyJpegZip } from './downloadStudyJpegZip';
import {
  addCurrentViewportImageToSelection,
  downloadSelectedJpeg,
} from './downloadSelectedJpeg';
import { DOWNLOAD_MESSAGES, formatExportProgress } from './downloadMessages';
import {
  showDownloadError,
  showDownloadSuccess,
  showExportInProgressNotice,
} from './downloadNotifications';
import type { DownloadExportKind } from '../../stores/useDownloadExportStore';
import type { DicomZipProgressCallback } from './types';

export type ViewportDownloadContext = {
  viewportId: string;
  servicesManager: AppTypes.ServicesManager;
  extensionManager: AppTypes.ExtensionManager;
  onProgress?: DicomZipProgressCallback;
};

export type DownloadHandlerResult = {
  success: boolean;
  message?: string;
  filename?: string;
  skipped?: boolean;
};

export type DownloadDicomZipHandler = (
  context: ViewportDownloadContext
) => Promise<DownloadHandlerResult>;

export type DownloadJpegHandler = (
  context: ViewportDownloadContext
) => Promise<DownloadHandlerResult>;

function beginDownloadExport(
  servicesManager: AppTypes.ServicesManager,
  exportKind: NonNullable<DownloadExportKind>,
  initialLabel: string
): boolean {
  const browserSupport = getBrowserDownloadSupport();

  if (!browserSupport.supported) {
    showDownloadError(servicesManager, browserSupport.message);
    return false;
  }

  if (!useDownloadExportStore.getState().tryStartExport(exportKind, initialLabel)) {
    showExportInProgressNotice(servicesManager);
    return false;
  }

  return true;
}

function endDownloadExport(): void {
  useDownloadExportStore.getState().endExport();
}

function createProgressUpdater(
  exportKind: NonNullable<DownloadExportKind>,
  onProgress?: DicomZipProgressCallback
): DicomZipProgressCallback {
  const baseLabel =
    exportKind === 'dicom' ? DOWNLOAD_MESSAGES.PREPARING_ZIP : DOWNLOAD_MESSAGES.PREPARING_JPEG;

  return (completed, total) => {
    const label = formatExportProgress(completed, total, baseLabel);
    useDownloadExportStore.getState().setProgressLabel(label);
    onProgress?.(completed, total);
  };
}

async function runGuardedDownload(
  context: ViewportDownloadContext,
  exportKind: NonNullable<DownloadExportKind>,
  initialLabel: string,
  task: (onProgress?: DicomZipProgressCallback) => Promise<
    | { success: true; filename: string; imageCount?: number; instanceCount?: number }
    | { success: false; message: string }
  >,
  notificationKind: 'dicom' | 'jpeg'
): Promise<DownloadHandlerResult> {
  if (!beginDownloadExport(context.servicesManager, exportKind, initialLabel)) {
    return { success: false, skipped: true };
  }

  try {
    const onProgress = createProgressUpdater(exportKind, context.onProgress);
    const result = await task(onProgress);

    if (result.success) {
      const exportCount = result.imageCount ?? result.instanceCount;
      showDownloadSuccess(
        context.servicesManager,
        result.filename,
        notificationKind,
        exportCount
      );
      return { success: true, filename: result.filename };
    }

    showDownloadError(context.servicesManager, result.message);
    return { success: false, message: result.message };
  } catch (error) {
    const message = classifyDownloadError(error);
    showDownloadError(context.servicesManager, message);
    return { success: false, message };
  } finally {
    endDownloadExport();
  }
}

export const downloadDicomZip: DownloadDicomZipHandler = async context =>
  runGuardedDownload(
    context,
    'dicom',
    DOWNLOAD_MESSAGES.PREPARING_ZIP,
    onProgress =>
      downloadStudyDicomZip({
        viewportId: context.viewportId,
        servicesManager: context.servicesManager,
        onProgress,
      }),
    'dicom'
  );

export const downloadCurrentJpeg: DownloadJpegHandler = async context =>
  runGuardedDownload(
    context,
    'jpeg-current',
    DOWNLOAD_MESSAGES.PREPARING_JPEG,
    () =>
      downloadCurrentImageJpeg({
        viewportId: context.viewportId,
        servicesManager: context.servicesManager,
        extensionManager: context.extensionManager,
      }),
    'jpeg'
  );

export const downloadStudyJpeg: DownloadJpegHandler = async context =>
  runGuardedDownload(
    context,
    'jpeg-study',
    DOWNLOAD_MESSAGES.PREPARING_JPEG,
    onProgress =>
      downloadStudyJpegZip({
        viewportId: context.viewportId,
        servicesManager: context.servicesManager,
        extensionManager: context.extensionManager,
        onProgress,
      }),
    'jpeg'
  );

export const downloadSelectedJpegHandler: DownloadJpegHandler = async context =>
  runGuardedDownload(
    context,
    'jpeg-selected',
    DOWNLOAD_MESSAGES.PREPARING_JPEG,
    onProgress =>
      downloadSelectedJpeg({
        viewportId: context.viewportId,
        servicesManager: context.servicesManager,
        extensionManager: context.extensionManager,
        onProgress,
      }),
    'jpeg'
  );

export function addCurrentImageToJpegSelection(
  context: Pick<ViewportDownloadContext, 'viewportId' | 'servicesManager'>
): DownloadHandlerResult {
  const browserSupport = getBrowserDownloadSupport();

  if (!browserSupport.supported) {
    showDownloadError(context.servicesManager, browserSupport.message);
    return { success: false, message: browserSupport.message };
  }

  const result = addCurrentViewportImageToSelection(context.viewportId, context.servicesManager);

  if (!result.success) {
    showDownloadError(context.servicesManager, result.message);
    return { success: false, message: result.message };
  }

  return { success: true };
}

/** @deprecated Use downloadDicomZip */
export const placeholderDownloadDicomZip = downloadDicomZip;

/** @deprecated Use downloadCurrentJpeg or downloadStudyJpeg */
export const placeholderDownloadJpeg = downloadCurrentJpeg;

export { DOWNLOAD_MESSAGES };
